/* Ticket Events tab , edit Adult/Child prices AND pause/resume ticket sales.
   Live prices apply to ALL future Stripe Checkout sessions (Vivid/NYE/Aus Day/Boxing Day).
   The "Sales" toggle instantly stops or restarts checkouts for a specific event
   (e.g. mark NYE Sold Out once the boat fills up). Inline edit on blur. */
import React, { useCallback, useEffect, useState } from "react";
import { Ticket, RefreshCcw, Check, Ban, Loader2 } from "lucide-react";
import { adminApi } from "./adminApi";

function PriceCell({ value, onSave, prefix = "$", testid }) {
 const [local, setLocal] = useState(value);
 const [editing, setEditing] = useState(false);
 const [savedAt, setSavedAt] = useState(0);
 const [err, setErr] = useState("");

 useEffect(() => { if (!editing) setLocal(value); }, [value, editing]);
 useEffect(() => {
  if (!savedAt) return;
  const t = setTimeout(() => setSavedAt(0), 2200);
  return () => clearTimeout(t);
 }, [savedAt]);

 const commit = async () => {
  setEditing(false);
  const next = parseFloat(local);
  if (Number.isNaN(next) || next < 0) { setErr("invalid"); setLocal(value); return; }
  if (Number(next) === Number(value)) return;
  try { await onSave(next); setSavedAt(Date.now()); setErr(""); }
  catch (e) { setErr(e.message || "Save failed"); setLocal(value); }
 };

 if (editing) {
  return (
   <span className="inline-flex items-center gap-1">
    <span className="text-white/45">{prefix}</span>
    <input
     autoFocus
     type="number"
     step="0.01"
     min="0"
     value={local}
     onChange={(e) => setLocal(e.target.value)}
     onBlur={commit}
     onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setLocal(value); setEditing(false); } }}
     className="bg-white/[0.08] border border-amber-300/50 rounded-lg px-2 py-1 outline-none text-white text-lg font-bold w-24 tabular-nums focus:ring-2 focus:ring-amber-300/40"
     data-testid={testid ? `${testid}-input` : undefined}
    />
   </span>
  );
 }

 return (
  <span className="inline-flex items-center gap-2 group">
   <button
    type="button"
    onClick={() => setEditing(true)}
    className="text-white font-black text-lg lg:text-xl tabular-nums hover:bg-amber-200/10 rounded px-2 py-1 -mx-2 transition-colors"
    data-testid={testid}
   >
    {prefix}{Number(local).toLocaleString("en-AU", { minimumFractionDigits: Number(local) % 1 ? 2 : 0 })}
   </button>
   {savedAt ? (
    <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] uppercase tracking-wider font-black" data-testid={testid ? `${testid}-saved` : undefined}>
     <Check className="w-3 h-3" /> Saved
    </span>
   ) : null}
   {err && !savedAt ? <span className="text-rose-300 text-[10px] uppercase font-black">! {err}</span> : null}
  </span>
 );
}

export default function AdminTicketEvents({ onUnauthorized, showToast }) {
 const [events, setEvents] = useState([]);
 const [loading, setLoading] = useState(true);
 const [togglingKey, setTogglingKey] = useState(null);

 const load = useCallback(async () => {
  setLoading(true);
  try {
   const res = await adminApi("/admin/cms/ticket-events");
   setEvents(res.events || []);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Failed to load ticket events", "err");
  } finally { setLoading(false); }
 }, [onUnauthorized, showToast]);

 useEffect(() => { load(); }, [load]);

 const updatePrice = async (key, field, value, existing) => {
  const body = { adult: existing.adult, child: existing.child };
  body[field] = value;
  try {
   const updated = await adminApi(`/admin/cms/ticket-events/${key}`, { method: "PUT", body });
   setEvents((prev) => prev.map((e) => e.key === key ? updated : e));
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); }
   throw e;
  }
 };

 const togglePause = async (ev) => {
  const next = !ev.sales_paused;
  const label = ev.name || ev.key;
  if (next && !window.confirm(`Pause ticket sales for "${label}"?\n\nNo new checkouts will be allowed and the public page will show SOLD OUT. Existing paid orders are unaffected.`)) return;
  setTogglingKey(ev.key);
  try {
   const updated = await adminApi(`/admin/cms/ticket-events/${ev.key}/pause`, {
    method: "PATCH", body: { sales_paused: next },
   });
   setEvents((prev) => prev.map((e) => e.key === ev.key ? updated : e));
   showToast(next ? `${label} — sales PAUSED (Sold Out).` : `${label} — sales resumed.`, "ok");
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Toggle failed", "err");
  } finally { setTogglingKey(null); }
 };

 return (
  <div data-testid="admin-tab-ticket-events">
   <div className="flex items-start justify-between gap-4 mb-6">
    <div>
     <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-2">
      <Ticket className="w-3.5 h-3.5" /> Ticketed Events
     </div>
     <p className="text-white/65 text-sm max-w-2xl">
      Live prices for every Stripe-driven ticket sale. Click any number to edit prices, or flip the <strong className="text-white">Sales</strong> switch to mark an event as <strong className="text-rose-200">Sold Out</strong> — new checkouts will be blocked immediately.
     </p>
    </div>
    <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-events-refresh"><RefreshCcw className="w-3 h-3" /> Refresh</button>
   </div>

   {loading ? (
    <div className="text-white/45 text-sm py-12 text-center">Loading…</div>
   ) : (
    <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden">
     <div className="grid grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-x-4 px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-black text-white/45 border-b border-white/10">
      <div>Event</div>
      <div className="text-right">Adult</div>
      <div className="text-right">Child</div>
      <div className="text-right">Sales</div>
     </div>
     {events.map((e, i) => {
      const paused = !!e.sales_paused;
      const isToggling = togglingKey === e.key;
      return (
      <div key={e.key} className={`grid grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-x-4 px-5 py-5 items-center ${i < events.length - 1 ? "border-b border-white/[0.06]" : ""} ${paused ? "bg-rose-500/[0.04]" : ""}`} data-testid={`admin-event-row-${e.key}`}>
       <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
         <span className="text-white font-bold text-sm lg:text-base">{e.name || e.key}</span>
         {paused && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/25 border border-rose-400/50 text-rose-100 text-[9px] uppercase tracking-[0.18em] font-black" data-testid={`admin-event-${e.key}-soldout-badge`}>
           <Ban className="w-2.5 h-2.5" /> Sold Out
          </span>
         )}
        </div>
        {e.note && <div className="text-white/45 text-xs mt-0.5">{e.note}</div>}
       </div>
       <div className="text-right">
        <PriceCell
         value={e.adult}
         onSave={(v) => updatePrice(e.key, "adult", v, e)}
         testid={`admin-event-${e.key}-adult`}
        />
       </div>
       <div className="text-right">
        <PriceCell
         value={e.child}
         onSave={(v) => updatePrice(e.key, "child", v, e)}
         testid={`admin-event-${e.key}-child`}
        />
       </div>
       <div className="flex items-center justify-end">
        <button
         type="button"
         onClick={() => togglePause(e)}
         disabled={isToggling}
         aria-pressed={!paused}
         className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-all disabled:opacity-50 ${
          paused
           ? "bg-rose-500/15 border-rose-400/50 hover:bg-rose-500/25 text-rose-100"
           : "bg-emerald-500/15 border-emerald-400/50 hover:bg-emerald-500/25 text-emerald-100"
         }`}
         data-testid={`admin-event-${e.key}-pause-toggle`}
        >
         <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${paused ? "bg-rose-500/60" : "bg-emerald-500/60"}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${paused ? "left-0.5" : "left-4"}`} />
         </span>
         <span className="text-[10px] uppercase tracking-[0.22em] font-black">
          {isToggling ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (paused ? "Sold out" : "Live")}
         </span>
        </button>
       </div>
      </div>
      );
     })}
    </div>
   )}
  </div>
 );
}
